/**
 * PWA / Native App File Picker Recovery
 *
 * Handles iOS crashes when opening the camera via <input type="file">.
 * - PWA (standalone): iOS may kill the WebView process to reclaim memory.
 * - Capacitor native app: Missing Info.plist permissions cause a crash (fixed separately).
 *   Recovery still helps if the app restarts after a background termination.
 *
 * Centralized approach: call installGlobalFilePickerProtection() once at app startup.
 * It uses event delegation to protect ALL file inputs automatically — no per-component
 * changes needed.
 */

import { isIOS, isPWA, isNativeApp } from './pwa';

const RECOVERY_KEY = 'pwa-file-picker-recovery';
const RECOVERY_MAX_AGE_MS = 2 * 60 * 1000; // 2 minutes — camera session timeout

interface RecoveryState {
  url: string;
  timestamp: number;
}

/**
 * Save the current URL before opening a file picker.
 */
function saveFilePickerState(): void {
  const state: RecoveryState = {
    url: window.location.pathname + window.location.search,
    timestamp: Date.now(),
  };

  try {
    sessionStorage.setItem(RECOVERY_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage may be full or unavailable
  }
}

/**
 * Clear saved recovery state.
 */
function clearFilePickerState(): void {
  try {
    sessionStorage.removeItem(RECOVERY_KEY);
  } catch {
    // ignore
  }
}

/**
 * Install a global click listener (capture phase) that saves navigation state
 * whenever ANY <input type="file"> is activated. Uses event delegation so it
 * automatically covers every file input in the app — present and future.
 *
 * Activates on iOS PWA and Capacitor native apps.
 * Safe to call multiple times (idempotent).
 */
let installed = false;
export function installGlobalFilePickerProtection(): void {
  if (installed) return;
  if (!isIOS()) return;
  if (!isPWA() && !isNativeApp()) return;
  installed = true;

  // Capture phase so we run before the browser opens the file picker
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'file') {
      saveFilePickerState();
    }
  }, true);

  // Clear recovery state when a file is successfully selected
  document.addEventListener('change', (e) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'file') {
      clearFilePickerState();
    }
  }, true);
}

/**
 * Check for saved file picker state and navigate back if the app was killed
 * and restarted by iOS. Should be called once on app startup.
 *
 * @param navigate - React Router navigate function
 * @returns true if recovery navigation occurred
 */
export function checkAndRecoverFilePickerState(
  navigate: (path: string) => void
): boolean {
  try {
    const raw = sessionStorage.getItem(RECOVERY_KEY);
    if (!raw) return false;

    const state: RecoveryState = JSON.parse(raw);
    clearFilePickerState();

    // Only recover if the saved state is recent (within the timeout window)
    const age = Date.now() - state.timestamp;
    if (age > RECOVERY_MAX_AGE_MS) return false;

    // Only recover if the current URL is different (i.e., the app restarted at root)
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath === state.url) return false;

    // Navigate back to the saved URL
    navigate(state.url);
    return true;
  } catch {
    clearFilePickerState();
    return false;
  }
}
